import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../common/Modal';
import DocumentUploadInput from '../common/DocumentUploadInput';
import './ModalForms.css';

const emptyService = {
  operatorId: '',
  type: '',
  description: '',
  price: '',
  currency: 'USD',
  conversion: '',
  dateOut: '',
  dateIn: '',
  reservationDocument: null
};

const formatTwoDecimals = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return '';
  return parsed.toFixed(2);
};

const ServiceModal = ({ isOpen, onClose, onSave, initialData, operators, defaultDateOut, defaultDateIn }) => {
  const [formData, setFormData] = useState(emptyService);
  const conversionDisabled = useMemo(() => formData.currency !== '$', [formData.currency]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        operatorId: initialData.operator_id || initialData.operatorId || '',
        type: initialData.type || '',
        description: initialData.description || '',
        price: initialData.price ?? '',
        currency: initialData.currency || '$',
        conversion: formatTwoDecimals(initialData.conversion),
        dateOut: initialData.date_out ? initialData.date_out.slice(0, 10) : (initialData.dateOut || ''),
        dateIn: initialData.date_in ? initialData.date_in.slice(0, 10) : (initialData.dateIn || ''),
        reservationDocument: null
      });
    } else {
      setFormData({
        ...emptyService,
        dateOut: defaultDateOut || '',
        dateIn: defaultDateIn || ''
      });
    }
  }, [initialData, isOpen, defaultDateOut, defaultDateIn]);

  useEffect(() => {
    if (conversionDisabled) {
      setFormData((prev) => ({ ...prev, conversion: '' }));
    }
  }, [conversionDisabled]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave({
      ...formData,
      conversion:
        formData.currency === '$' && formData.conversion !== ''
          ? Number(formatTwoDecimals(formData.conversion))
          : null
    });
  };

  return (
    <Modal
      title={initialData ? 'Editar Servicio' : 'Nuevo Servicio'}
      isOpen={isOpen}
      onClose={onClose}
      onEnter={() => handleSubmit()}
      footer={(
        <div className="modal-footer">
          <div className="footer-actions">
            <button type="button" className="btn secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" form="service-modal-form" className="btn">Guardar</button>
          </div>
        </div>
      )}
    >
      <form id="service-modal-form" className="modal-form" onSubmit={handleSubmit}>
        <div className="form-grid two-columns">
          <label>
            Fecha de salida
            <input type="date" name="dateIn" value={formData.dateIn} onChange={handleChange} disabled />
          </label>
          <label>
            Fecha de vuelta
            <input type="date" name="dateOut" value={formData.dateOut} onChange={handleChange} disabled />
          </label>
          <label style={{ gridColumn: '1 / -1' }}>
            Descripción
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="textarea-tall"
              required
            />
          </label>
          <label>
            Tipo
            <select name="type" value={formData.type} onChange={handleChange} required>
              <option value="">Selecciona</option>
              <option value="Vuelo">Vuelo</option>
              <option value="Hospedaje">Hospedaje</option>
              <option value="Asistencia">Asistencia</option>
              <option value="Paquete">Paquete</option>
              <option value="Otro">Otro</option>
            </select>
          </label>
          <label>
            Operador
            <select name="operatorId" value={formData.operatorId} onChange={handleChange} required>
              <option value="">Selecciona</option>
              {operators.map((op) => (
                <option key={op.id} value={op.id}>{op.name}</option>
              ))}
            </select>
          </label>
          <label>
            Precio
            <input type="number" step="0.01" name="price" value={formData.price} onChange={handleChange} required />
          </label>
          <label>
            Moneda
            <select name="currency" value={formData.currency} onChange={handleChange}>
              <option value="$">$</option>
              <option value="USD">USD</option>
            </select>
          </label>
          <label>
            Conversión
            <input
              type="number"
              step="0.01"
              name="conversion"
              value={formData.conversion}
              onChange={handleChange}
              onBlur={(e) =>
                setFormData((prev) => ({ ...prev, conversion: formatTwoDecimals(e.target.value) }))
              }
              disabled={conversionDisabled}
              required={!conversionDisabled}
              placeholder={conversionDisabled ? '—' : 'Tipo de cambio'}
            />
          </label>
          <DocumentUploadInput
            fullWidth
            title="Reserva"
            hasFile={Boolean(formData.reservationDocument) || Number(initialData?.has_reservation_document) === 1}
            description={formData.reservationDocument?.name || (
              Number(initialData?.has_reservation_document) === 1
                ? 'Documento cargado'
                : 'Sin archivo cargado'
            )}
            inputLabel="Seleccionar documento de Reserva"
            buttonLabel={formData.reservationDocument ? 'Cambiar archivo' : 'Subir archivo'}
            helperText={Number(initialData?.has_reservation_document) === 1
              ? 'Si elegís otro archivo, reemplazará la Reserva actual.'
              : undefined}
            onFileSelected={(file) => setFormData((previous) => ({ ...previous, reservationDocument: file }))}
          />
        </div>
      </form>
    </Modal>
  );
};

export default ServiceModal;
